import { Box, Stack, type Theme, useMediaQuery } from '@mui/material';
import Tabs from '/@/components/bayer/tabs';
import { markNotificationTrackerAsRead, useNotificationTrackersByCodename } from '/@/hooks/notification-tracker';
import { _ } from '/@/locale';
import { objectEntries } from '/@/utils';
import type { GrowersTabType, useTabs } from '../../..';
import GrowerDashboardCurrentCampaign from './tabs/current-campaign';
import GrowerDashboardPastReports from './tabs/past-reports';
import GrowerDashboardRecommendations from './tabs/recommendations';

const GrowerDashboard = ({
    growerNodeId,
    tab,
    handleTabChange,
    showNewFieldButton = false,
    fieldsTourAnchors = false,
}: {
    growerNodeId: number;
    tab: ReturnType<typeof useTabs>['tab'];
    handleTabChange: ReturnType<typeof useTabs>['handleTabChange'];
    showNewFieldButton?: boolean;
    /** Mis Lotes driver tour anchors (consultant fields route, RV / Advisor only). */
    fieldsTourAnchors?: boolean;
}) => {
    const notificationTrackersQueryModal = useNotificationTrackersByCodename({
        codenameIcontains: 'cm_automatic_close_chip_',
    }).data?.results?.filter((r) => !r.readedOn);
    const tabs: Record<
        GrowersTabType,
        { label: string; disabled: boolean; component?: React.ReactNode; chipCount?: number; dataTestId?: string }
    > = {
        'current-campaign': {
            label: _('Campaña actual'),
            disabled: false,
            component: (
                <GrowerDashboardCurrentCampaign
                    growerNodeId={growerNodeId}
                    withCards={true}
                    showNewFieldButton={showNewFieldButton}
                    fieldsTourAnchors={fieldsTourAnchors}
                />
            ),
        },
        'past-reports': {
            label: _('Reportes de campaña'),
            disabled: false,
            component: <GrowerDashboardPastReports growerNodeId={growerNodeId} />,
            chipCount: notificationTrackersQueryModal?.length,
        },
        recommendations: {
            label: _('Recomendaciones'),
            disabled: false,
            component: <GrowerDashboardRecommendations growerNodeId={growerNodeId} />,
        },
    } as const;

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const tabsEl = (
        <Tabs
            options={objectEntries(tabs).map(([value, tab]) => ({
                label: tab.label,
                value,
                disabled: tab.disabled,
                chipCount: tab.chipCount,
                dataTestId: tab.dataTestId,
            }))}
            value={tab}
            setValue={(value) => {
                handleTabChange(value);
                if (value === 'past-reports') {
                    notificationTrackersQueryModal?.map((r) => markNotificationTrackerAsRead(r.id));
                }
            }}
            tabsVariant={isMobile ? 'scrollable' : 'fullWidth'}
        />
    );

    return (
        <Stack direction='column' spacing={3}>
            {fieldsTourAnchors ? <Box id='tabs-bar-l'>{tabsEl}</Box> : tabsEl}
            {tabs[tab].component}
        </Stack>
    );
};

export default GrowerDashboard;
